import React, { useEffect, useMemo, useRef, useState } from 'react'
import * as yup from 'yup'
import { useDispatch } from 'react-redux'
import { useMakeSelector, useSelector } from 'hooks/redux'
import { prop } from 'ramda'
import styled from '@emotion/styled'
import strings from 'utils/strings'
import { LIST, DASH_VIEW } from 'utils/constants'

import { addSwitchToLocation } from 'actions/dash'
import { getSmartSwitches } from 'selectors/dash'

import {
  Paragraph,
  RadioButtonGroup,
  SelectMenu,
  Button,
  FlexRow,
  FlexCol,
} from 'components/patterns'
import { sortOptions } from 'components/patterns/Inputs/SelectMenu'
import { Content, Footer } from 'components/SwitchAndGauge/DashDrawer/Sections'
import PanelPartDetails from 'components/SwitchAndGauge/DashDrawer/PanelPartDetails'
import { makeComponentMetaSelector } from 'selectors/navigator'
import { setSelectedIdWithDrawerAndMeta } from 'actions/navigator'
import { StyledViewWrap } from './style'

const createOptions = (switches, title) =>
  switches.map((component) => ({
    key: prop('panelNumber', component),
    title: prop(title, component),
    value: prop('panelNumber', component),
    disabled: prop('alreadyOn', component),
  }))

const PANEL_NUMBER = 'PANEL_NUMBER'
const PANEL_DESCRIPTION = 'PANEL_DESCRIPTION'
const ADD_SWITCH_MAX_LENGTH = 80

const AddView = () => {
  const dispatch = useDispatch()
  const selectWrapperRef = useRef(null)

  const [selectedBtn, setSelectedBtn] = useState(PANEL_NUMBER)
  const [selectedSwitch, setSelectedSwitch] = useState('')
  const { id: location } = useMakeSelector(makeComponentMetaSelector, {
    id: DASH_VIEW,
  })
  const switches = useSelector(getSmartSwitches, location)
  const switchesArray = useMemo(() => Object.values(switches), [switches])
  const byPanelNumber = useMemo(
    () => createOptions(switchesArray, 'panelNumber').sort(sortOptions()),
    [switchesArray]
  )
  const byPanelDescription = useMemo(
    () => createOptions(switchesArray, 'panelDescription').sort(sortOptions()),
    [switchesArray]
  )
  const menuOptions =
    selectedBtn === PANEL_NUMBER ? byPanelNumber : byPanelDescription

  useEffect(() => {
    const input =
      selectWrapperRef.current &&
      selectWrapperRef.current.querySelector('input')
    if (input) {
      input.setAttribute('maxlength', ADD_SWITCH_MAX_LENGTH)
    }
  }, [])

  const handleCancel = () => {
    dispatch(
      setSelectedIdWithDrawerAndMeta(DASH_VIEW, {
        view: LIST,
      })
    )
  }

  const handleAdd = () => {
    dispatch(
      setSelectedIdWithDrawerAndMeta(DASH_VIEW, {
        view: LIST,
      })
    )
    // ToDo: Refactor that switch is added to REDUX.dash.stagedSwitch
    dispatch(addSwitchToLocation(selectedSwitch, location))
  }

  const handleInputChange = (value) => {
    setSelectedSwitch(value)
  }

  const handleToggleView = (e) => {
    const { value } = e.target
    setSelectedBtn(value)
  }

  const addSwitch = strings.add_switch

  const isAddDisabled = !selectedSwitch

  return (
    <StyledViewWrap>
      <Content>
        <HeaderContainer>
          <StyledParagraphOne>{addSwitch}</StyledParagraphOne>
        </HeaderContainer>
        <StyledRadioButtonGroup
          onChange={handleToggleView}
          currentValue={selectedBtn}
          buttons={[
            {
              value: PANEL_NUMBER,
              text: strings.part_no,
            },
            {
              value: PANEL_DESCRIPTION,
              text: strings.description,
            },
          ]}
        />
        <div ref={selectWrapperRef} data-testid="select-wrapper">
          <StyledSelectMenu
            showSearch
            name={strings.select}
            placeholder={strings.select_a_part}
            value={selectedSwitch}
            onChange={handleInputChange}
            filterOption
            defaultActiveFirstOption={false}
            className="form-field"
            options={menuOptions}
            rules={yup.string().nullable()}
          />
        </div>
        {selectedSwitch && (
          <StyledPanelPartDetails partOverride={switches[selectedSwitch]} />
        )}
      </Content>
      <Footer>
        <FlexRow>
          <FlexCol span={12}>
            <div>
              <Button type="secondary" onClick={handleCancel}>
                {strings.cancel}
              </Button>
            </div>
          </FlexCol>
          <FlexCol span={12} alignItems="end">
            <div>
              <Button
                type="primary"
                onClick={handleAdd}
                disabled={isAddDisabled}
              >
                {addSwitch}
              </Button>
            </div>
          </FlexCol>
        </FlexRow>
      </Footer>
    </StyledViewWrap>
  )
}

export default AddView

const HeaderContainer = styled.div``

const StyledParagraphOne = styled(Paragraph)`
  font-size: 20px;
  display: inline-block;
`

const StyledSelectMenu = styled(SelectMenu)`
  .ant-select {
    width: 100%;
  }
`
const StyledPanelPartDetails = styled(PanelPartDetails)`
  margin-top: 32px;
`

const StyledRadioButtonGroup = styled(RadioButtonGroup)`
  margin: 16px 0;
`
